import { defineStore } from "pinia";

import { DEFAULT_API_ERROR_MESSAGE } from "@/common/constants";
import { errorTypeToMessage } from "@/common/mappers";
import {
  Anstalldarende,
  ArendeType,
  type IKundserviceAppApiClient,
  KanalType,
  KundgruppType,
  LamnaOverType,
  SimpleAnstalldarende,
  ValidationProblemDetails,
  ValueResult_1OfAnstalldarende,
} from "@/composables/KundserviceAppApiClient";
import type { ApiError } from "@/types/ApiError";

export type State = {
  arende: ArendeState;
  arenden: ArendenState;
  populatedArenden: {
    isLoading: boolean;
    hasFetched: boolean;
    data: Record<number, Anstalldarende>;
    error: ApiError | null;
    throttled: boolean;
  };
};

type ArendeState = {
  isLoading: boolean;
  isSaving: boolean;
  data: Anstalldarende | null;
  error: ApiError | null;
};

type ArendenState = {
  isLoading: boolean;
  data: SimpleAnstalldarende[];
  error: ApiError | null;
};

export type CreateArendeInfo = {
  puzzelId: string | undefined;
  individId: string | undefined;
  email: string | undefined;
  telefonnummer: string | undefined;
  kontaktorsakId: string;
  kontaktorsakNamn: string;
  fortydligadKontaktorsak: string;
  samtalsamneIds: string[];
  internalNote: string;
  conversation: string | undefined;
  isVerified: boolean;
  isManuallyVerified: boolean;
  isClosed: boolean;
  kundgrupp: KundgruppType;
  arendetyp: ArendeType;
  lamnaOverTill: LamnaOverType;
  kanal: KanalType;
  ombud: boolean;
};

export type UpdateArendeInfo = {
  arendeId: number;
  puzzelId: string | undefined;
  individId: string;
  email: string | undefined;
  telefonnummer: string | undefined;
  kontaktorsakId: string;
  kontaktorsakNamn: string;
  fortydligadKontaktorsak: string;
  samtalsamneIds: string[];
  internalNote: string;
  conversation: string | undefined;
  isVerified: boolean;
  isManuallyVerified: boolean;
  isClosed: boolean;
  arendetyp: ArendeType;
  lamnaOverTill: LamnaOverType;
  kundgrupp: KundgruppType;
};

export type ReopenArendeInfo = {
  arendeId: number;
  individId: string;
  isVerified: boolean;
  isManuallyVerified: boolean;
};

export type UpdateConversationInfo = {
  arendeId: number;
  message: string;
  isVerified: boolean;
  isManuallyVerified: boolean;
};

export type CreateUnlinkedArendeInfo = {
  puzzelId: string | undefined;
  kontaktorsakId: string;
  kontaktorsakNamn: string;
  fortydligadKontaktorsak: string;
  samtalsamneIds: string[];
  internalNote: string;
  isClosed: boolean;
  kundgrupp: KundgruppType;
  arendetyp: ArendeType;
  lamnaOverTill: LamnaOverType;
  kanal: KanalType;
  ombud: boolean;
};

export const useAnstalldarendeStore = defineStore("anstalldaArenden", {
  state: (): State => {
    return {
      arende: {
        isLoading: false,
        isSaving: false,
        data: null,
        error: null,
      },
      arenden: {
        isLoading: false,
        data: [],
        error: null,
      },
      populatedArenden: {
        isLoading: false,
        hasFetched: false,
        data: {},
        error: null,
        throttled: false,
      },
    };
  },
  getters: {
    arendenCount(state) {
      return state.arenden.data.length;
    },
    hasArenden(state) {
      return state.arenden.data.length > 0;
    },
    openArenden(state) {
      return state.arenden.data.filter((a) => a.isOpen);
    },
    closedArenden(state) {
      return state.arenden.data.filter((a) => !a.isOpen);
    },
    openPreviewArenden(state) {
      return state.arenden.data.filter(
        (arende) => arende.isOpen && arende.id !== state.arende.data?.id,
      );
    },
    closedPreviewArenden(state) {
      return state.arenden.data
        .filter(
          (arende) => !arende.isOpen && arende.id !== state.arende.data?.id,
        )
        .sort((a, b) => b.updatedAt!.getTime() - a.updatedAt!.getTime())
        .slice(0, 2);
    },
    getPopulatedArende(state) {
      return (arendeId: number) => state.populatedArenden.data[arendeId];
    },
  },
  actions: {
    async fetchArenden(individId: string, apiClient: IKundserviceAppApiClient) {
      try {
        this.$patch((store) => {
          store.arenden.isLoading = true;
          store.arenden.error = null;
        });
        const arenden = await apiClient.getArendenByIndivid(individId);
        this.$patch((store) => {
          store.arenden.data = arenden;
        });
      } catch (e) {
        this.$patch((store) => {
          store.arenden.isLoading = false;
          store.arenden.error = e || "Error.";
        });
      } finally {
        this.$patch((store) => {
          store.arenden.isLoading = false;
        });
      }
    },
    async ensurePreviewArenden(apiClient: IKundserviceAppApiClient) {
      try {
        if (
          this.$state.populatedArenden.isLoading ||
          this.$state.populatedArenden.throttled
        ) {
          return;
        }

        const previewArenden: SimpleAnstalldarende[] = [
          ...this.openPreviewArenden,
          ...this.closedPreviewArenden,
        ];

        const missingArenden = previewArenden.filter(
          (a) => !(a.id in this.$state.populatedArenden.data),
        );

        if (missingArenden.length > 0) {
          this.$state.populatedArenden.isLoading = true;

          const previewArenden = await apiClient.getArendenByIds(
            missingArenden.map((arende) => arende.id),
          );

          const newPreviewArendenDict = previewArenden.reduce(
            (acc: Record<number, Anstalldarende>, arende) => {
              acc[arende.id] = arende;
              return acc;
            },
            {},
          );

          this.$patch((store) => {
            store.populatedArenden.hasFetched = true;
            store.populatedArenden.throttled = false;
            store.populatedArenden.data = {
              ...store.populatedArenden.data,
              ...newPreviewArendenDict,
            };
            previewArenden.forEach((arende) => {
              store.populatedArenden.data[arende.id] = arende;
            });
          });
        }
      } catch (e) {
        if (e instanceof ValidationProblemDetails && e.status === 429) {
          this.$state.populatedArenden.throttled = true;
        }
        this.$state.populatedArenden.error = e || "Error.";
      } finally {
        this.$state.populatedArenden.isLoading = false;
      }
    },
    async fetchArende(arendeId: number, apiClient: IKundserviceAppApiClient) {
      try {
        this.$patch((store) => {
          store.arende.isLoading = true;
          store.arende.error = null;
        });
        const arende = await apiClient.getArendeById(arendeId);
        this.$patch((store) => {
          store.arende.data = arende;
          store.populatedArenden.data[arende.id] = arende;
        });
        return arende;
      } catch (e) {
        this.$patch((store) => {
          store.arende.isLoading = false;
          store.arende.error = e || "Error.";
        });
      } finally {
        this.$patch((store) => {
          store.arende.isLoading = false;
        });
      }
    },
    async createArende(
      creationInfo: CreateArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });
        const arendeResponse = await apiClient.createAnstalldarende(
          creationInfo.puzzelId,
          creationInfo.telefonnummer,
          creationInfo.individId,
          creationInfo.email,
          creationInfo.kontaktorsakId,
          creationInfo.kontaktorsakNamn,
          creationInfo.fortydligadKontaktorsak,
          creationInfo.samtalsamneIds,
          creationInfo.internalNote,
          [],
          creationInfo.conversation ?? "",
          [],
          creationInfo.isClosed,
          creationInfo.isVerified,
          creationInfo.isManuallyVerified,
          creationInfo.kundgrupp,
          creationInfo.arendetyp,
          creationInfo.lamnaOverTill ?? LamnaOverType.Nej,
          creationInfo.kanal,
          creationInfo.ombud,
        );

        this.$patch((store) => {
          store.populatedArenden.data[arendeResponse.value!.id] =
            arendeResponse.value as Anstalldarende;
        });

        return arendeResponse.value;
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
    async createUnlinkedArende(
      creationInfo: CreateUnlinkedArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });
        const arendeResponse = await apiClient.createUnlinkedArende(
          creationInfo.puzzelId,
          creationInfo.kontaktorsakId,
          creationInfo.kontaktorsakNamn,
          creationInfo.fortydligadKontaktorsak,
          creationInfo.samtalsamneIds,
          creationInfo.internalNote,
          [],
          creationInfo.isClosed,
          creationInfo.kundgrupp,
          creationInfo.arendetyp,
          creationInfo.lamnaOverTill ?? LamnaOverType.Nej,
          creationInfo.kanal,
          creationInfo.ombud,
        );
        return arendeResponse.value;
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
    async updateArende(
      updateInfo: UpdateArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });
        const arendeResponse = await apiClient.ensureArende(
          updateInfo.arendeId,
          updateInfo.puzzelId,
          updateInfo.telefonnummer,
          updateInfo.individId,
          updateInfo.email,
          updateInfo.kontaktorsakId,
          updateInfo.kontaktorsakNamn,
          updateInfo.fortydligadKontaktorsak,
          updateInfo.samtalsamneIds,
          updateInfo.internalNote,
          [],
          updateInfo.conversation ?? "",
          [],
          updateInfo.isClosed,
          updateInfo.isVerified,
          updateInfo.isManuallyVerified,
          updateInfo.arendetyp,
          updateInfo.lamnaOverTill ?? LamnaOverType.Nej,
          updateInfo.kundgrupp,
        );

        return await this.fetchArende(arendeResponse.value!.id, apiClient);
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
    async reopenArende(
      reopenInfo: ReopenArendeInfo,
      apiClient: IKundserviceAppApiClient,
    ) {
      try {
        this.$patch((store) => {
          store.arende.isSaving = true;
          store.arende.error = null;
        });

        const reopenedArende = await apiClient.ensureArende(
          reopenInfo.arendeId,
          undefined,
          undefined,
          reopenInfo.individId,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false,
          reopenInfo.isVerified,
          reopenInfo.isManuallyVerified,
          undefined,
          undefined,
          undefined,
        );

        return await this.fetchArende(reopenedArende.value!.id, apiClient);
      } catch (e) {
        handleError(e, this.$state);
      } finally {
        this.$patch((store) => {
          store.arende.isSaving = false;
        });
      }
    },
  },
});

function handleError(e: unknown, state: State) {
  if (e instanceof ValueResult_1OfAnstalldarende && e.errors?.length > 0) {
    const error = e;

    state.arende.error = {
      status: error.status,
      errors: errorTypeToMessage(error.errors[0]),
    };
  } else {
    state.arende.error = {
      errors: DEFAULT_API_ERROR_MESSAGE,
    };
    console.log({ e });
  }
}
